// Dependencies
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { createStructuredSelector } from 'reselect';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import property from 'lodash/property';
// eslint-disable-next-line lodash/import-scope -- directive added automatically by Shepherd migration
import get from 'lodash/get';
import classNames from 'classnames';
import classes from '@nerdwallet/base-styles/classes';

import { Text, Box, Title } from '@nerdwallet/currency';
import Disclosure, { disclosureTypes } from '../disclosure/disclosure';
import parseMarkdownLinks from '../../lib/parse-md-links';
import Image from '../image/image';

import {
  textColorPicker,
  preheaderInput,
  boolCheckboxSpec,
  subtitleInput,
  stringSelectSpec,
  stringInputSpec,
  stringTextAreaSpec,
  numberInputSpec,
} from '../../lib/editing-configs';
import classColor from '../../lib/parse-color';
import parseStyleFormatting from './lib/parse-hero-formatting';

import BackgroundColor from '../background-color/background-color';
import SmartButton, { NULL_BUTTON_TYPE } from '../smart-button/smart-button';
import LinkButton from '../link-button/link-button';

import './standard-hero.less';

const StandardHero = ({
  backgroundSize,
  backgroundImageSrcDesktop,
  backgroundImageSrcTablet,
  backgroundImageSrcMobile,
  useParallax,
  parallaxForMobile,
  containerHeightDesktop,
  containerHeightMobile,
  reverseDesktop,
  bottomImage,
  bgOptions,
  useCenteredContent,
  parallaxOpacity,
  textColor,
  subtitle,
  subtitleMobile,
  disclosureType,
  disclosureColor,
  disclosureOnBackground,
  maxWidthDesktop,
  fullWidthMobile,
  parseLinks,
  preheader,
  title,
  titleSize,
  size,
  selectHeroImageType,
  standaloneImageAlt,
  standaloneImageText,
  fontChronicle,
  tall,
  textVerticalAlignment,

  // From SmartButton - use verbatim to allow bringing in propTypes and editingConfig
  buttonType,
  successURL,
  authDialogCopyOverrides,
  authDialogImageOverrides,
  buttonURL,
  buttonText,
  buttonParams,
  appStoreURL,
  googlePlayURL,
  primary,
  linkText,
  linkColor,
}) => {
  const linkParser = (content) =>
    parseLinks ? parseMarkdownLinks(content, false) : content;

  const heroImageSrcMap = {
    lg: backgroundImageSrcDesktop,
    md: backgroundImageSrcTablet,
    sm: backgroundImageSrcMobile,
  };
  const imgHolder = {
    backgroundImage: `url(${backgroundImageSrcMobile}),url(${backgroundImageSrcTablet}),url(${backgroundImageSrcDesktop})`,
    display: 'none',
  };
  const button = (
    <SmartButton
      buttonType={buttonType}
      successURL={successURL}
      authDialogCopyOverrides={authDialogCopyOverrides}
      authDialogImageOverrides={authDialogImageOverrides}
      buttonURL={buttonURL}
      buttonText={buttonText}
      buttonParams={buttonParams}
      buttonClass="standard-hero__button__main"
      appStoreURL={appStoreURL}
      googlePlayURL={googlePlayURL}
      primary={primary}
      displayBlock
    />
  );

  const extraText = <LinkButton linkText={linkText} color={linkColor} />;

  // center hero content if useCenteredContent is selected
  const contentAlignment = useCenteredContent
    ? 'standard-hero-centered__content'
    : 'standard-hero__content';

  // only extend centered content alignment when a background image is used;
  // do not extend centered content alignment for standalone hero image usage
  const contentClass =
    selectHeroImageType === 'background'
      ? contentAlignment
      : 'standard-hero__standalone-image';

  // use flexbox on hero container div if standalone hero image is used
  const heroImageTypeClass =
    selectHeroImageType === 'standalone'
      ? 'standard-hero__standalone-flex'
      : '';

  // center hero subtitle if useCenteredContent is selected
  const subtitleAlignment = useCenteredContent
    ? 'standard-hero-centered__subtitle'
    : 'standard-hero__subtitle';

  // only extend centered subtitle alignment when a background image is used;
  // do not extend centered subtitle alignment for standalone hero image usage
  const subtitleClass =
    selectHeroImageType === 'background' ? subtitleAlignment : '';

  const fullWidthPadding =
    selectHeroImageType === 'background' && fullWidthMobile;

  const parallaxBackground = useParallax
    ? {
        backgroundSize,
        boxShadow: parallaxOpacity,
      }
    : {};

  const textVerticalAlignmentStyle =
    selectHeroImageType === 'standalone' && textVerticalAlignment;

  const shouldDisplayParallax =
    selectHeroImageType === 'background' &&
    size &&
    (size !== 'sm' || parallaxForMobile) &&
    `url(${heroImageSrcMap[size]})`;

  const getContainerHeight = () =>
    parallaxForMobile && size !== 'lg'
      ? containerHeightMobile
      : containerHeightDesktop;

  return (
    <BackgroundColor bgOptions={bgOptions}>
      {(backgroundColor) => (
        <Box
          className={classNames('standard-hero', {
            'standard-hero__100': fullWidthPadding,
            'standard-hero__tall': tall,
            'standard-hero__parallax-styles': useParallax,
          })}
          style={{
            backgroundColor,
            // @tablet-min
            backgroundImage: shouldDisplayParallax,
            height: getContainerHeight() || 'auto',
            backgroundSize:
              selectHeroImageType === 'background' && `${backgroundSize}`,
            ...parallaxBackground,
          }}
        >
          <Box style={imgHolder} />
          <Box
            className={classNames(heroImageTypeClass, {
              'reverse-desktop': reverseDesktop,
            })}
          >
            <Box
              style={{ alignSelf: textVerticalAlignmentStyle }}
              className={classNames(`${contentClass}`, {
                'width__mobile--100': fullWidthMobile,
                width__default: !fullWidthMobile,
                'bottom-aligned': bottomImage,
                'max-width--desktop': maxWidthDesktop,
              })}
            >
              {disclosureType && disclosureType !== 'none' && (
                <Box className="standard-hero__advertiser-disclosure">
                  <Disclosure
                    type={disclosureType}
                    color={disclosureColor}
                    onBackground={disclosureOnBackground}
                  />
                </Box>
              )}
              <Box
                className={classNames([
                  'standard-hero__preheader',
                  classColor(textColor),
                ])}
              >
                <Text size="small">{preheader}</Text>
              </Box>
              <Title
                component="h1"
                size={titleSize}
                className={classNames([
                  'standard-hero__title',
                  classColor(textColor),
                  {
                    gotham: !fontChronicle,
                  },
                ])}
              >
                {parseStyleFormatting(title)}
              </Title>
              <Box
                className={classNames([subtitleClass, classColor(textColor)])}
              >
                <Text>
                  {(size === 'sm' && parseStyleFormatting(subtitleMobile)) ||
                    parseStyleFormatting(subtitle)}
                </Text>
              </Box>
              <Box
                className={classNames([
                  'standard-hero__button',
                  {
                    [classes('padding-top-3')]: buttonType !== NULL_BUTTON_TYPE,
                  },
                ])}
              >
                {button}
                {extraText}
              </Box>
              <Box>
                {selectHeroImageType === 'background' &&
                  backgroundImageSrcMobile &&
                  !parallaxForMobile && (
                    <Image
                      src={backgroundImageSrcMobile}
                      alt=""
                      className="standard-hero__mobile-bg"
                    />
                  )}
              </Box>
            </Box>
            {selectHeroImageType === 'standalone' && (
              <Box
                className={classNames('standalone-hero-container', {
                  'reverse-desktop': reverseDesktop,
                  'bottom-aligned': bottomImage && !tall,
                  'bottom-aliged-tall': bottomImage && tall,
                  'max-width--desktop': maxWidthDesktop,
                })}
              >
                <Image src={heroImageSrcMap[size]} alt={standaloneImageAlt} />
                <Box
                  className={classNames([
                    'standard-hero__standalone-text',
                    classColor(textColor),
                    {
                      'bottom-aligned': bottomImage,
                    },
                  ])}
                >
                  <Text size="small">{linkParser(standaloneImageText)}</Text>
                </Box>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </BackgroundColor>
  );
};

StandardHero.propTypes = {
  preheader: PropTypes.string,
  title: PropTypes.string,
  subtitle: PropTypes.string,
  subtitleMobile: PropTypes.string,
  backgroundSize: PropTypes.oneOf(['contain', 'cover', 'auto']),
  backgroundImageSrcDesktop: PropTypes.string,
  backgroundImageSrcTablet: PropTypes.string,
  backgroundImageSrcMobile: PropTypes.string,
  useCenteredContent: PropTypes.bool,
  disclosureType: PropTypes.oneOf([...disclosureTypes, 'none']),
  disclosureColor: PropTypes.string,
  disclosureOnBackground: PropTypes.bool,
  fullWidthMobile: PropTypes.bool,
  parseLinks: PropTypes.bool,
  reverseDesktop: PropTypes.bool,
  standAloneImageText: PropTypes.string,
  standaloneImageAlt: PropTypes.string,
  size: PropTypes.string,
  selectHeroImageType: PropTypes.oneOf(['background', 'standalone']),
  ...BackgroundColor.propTypes,
  textColor: PropTypes.string,
  titleSize: PropTypes.string,
  ...SmartButton.propTypes,
  ...LinkButton.propTypes,
  fontChronicle: PropTypes.bool,
  maxWidthDesktop: PropTypes.bool,
  tall: PropTypes.bool,
  bottomImage: PropTypes.bool,
  textVerticalAlignment: PropTypes.oneOf(['flex-start', 'center', 'flex-end']),
  children: PropTypes.node,
};

StandardHero.defaultProps = {
  preheader: '',
  title: 'Title goes here',
  subtitle:
    'Description (desktop) goes here. Mobile will default to this if no mobile subtitle provided.',
  ...BackgroundColor.defaultProps,
  backgroundSize: 'contain',
  disclosureType: 'none',
  useCenteredContent: false,
  backgroundImageSrcDesktop: '',
  backgroundImageSrcTablet: '',
  backgroundImageSrcMobile: '',
  fullWidthMobile: false,
  bottomImage: false,
  parseLinks: true,
  size: '',
  selectHeroImageType: 'background',
  standaloneImageAlt: '',
  standaloneImageText: '',
  fontChronicle: false,
  tall: false,
  textVerticalAlignment: 'flex-start',
  ...SmartButton.defaultProps,
  children: null,
};

StandardHero.getEditingConfig = (componentDocument) => {
  const parallaxInputConfig =
    get(componentDocument, 'props.useParallax', false) === true
      ? [
          stringSelectSpec(
            'parallaxOpacity',
            'Select Parallax Background Opacity',
            [
              { label: 'No Opacity', value: 'none' },
              {
                label: 'Dark Opacity',
                value: 'rgb(10 10 10 / 50%) 0px 0px 0px 100vw inset',
              },
              {
                label: 'Light Opacity',
                value: 'rgb(255 255 255 / 50%) 0px 0px 0px 100vw inset',
              },
            ],
            {
              help: "Selecting 'Image' will place an image in the hero, while selecting 'Background Image' will place the image as a background image in the hero container. ",
            }
          ),
          boolCheckboxSpec(
            'parallaxForMobile',
            'Display the parallax background effect for mobile?'
          ),
          numberInputSpec('containerHeightDesktop', 'Container Height Desktop'),
          numberInputSpec('containerHeightMobile', 'Container Height Mobile'),
        ]
      : [];
  const heroImageTypeInput =
    get(componentDocument, 'props.selectHeroImageType', '') === 'standalone'
      ? [
          stringInputSpec(
            'backgroundImageSrcDesktop',
            'Standalone Image URL (Desktop)'
          ),
          stringInputSpec(
            'backgroundImageSrcTablet',
            'Standalone Image URL (Tablet)'
          ),
          stringInputSpec(
            'backgroundImageSrcMobile',
            'Standalone Image URL (Mobile)'
          ),
          stringInputSpec('standaloneImageAlt', 'Standalone Image alt-tag'),
          stringInputSpec(
            'standaloneImageText',
            'Text or link placed below image'
          ),
          boolCheckboxSpec('reverseDesktop', 'Reverse desktop layout?'),
          boolCheckboxSpec(
            'bottomImage',
            'Align image to the container bottom'
          ),
          boolCheckboxSpec(
            'maxWidthDesktop',
            'Limit the width of the content to half (desktop)'
          ),
          stringSelectSpec('textVerticalAlignment', 'Text vertical alignment', [
            { label: 'Start', value: 'flex-start' },
            { label: 'Center', value: 'center' },
            { label: 'End', value: 'flex-end' },
          ]),
        ]
      : [
          boolCheckboxSpec('useCenteredContent', 'Center hero content?'),
          stringSelectSpec(
            'backgroundSize',
            'Background Size',
            [
              { label: 'Contain', value: 'contain' },
              { label: 'Cover', value: 'cover' },
              { label: 'Auto', value: 'auto' },
            ],
            {
              help: 'Only applies to Desktop and Tablet',
            }
          ),
          stringInputSpec(
            'backgroundImageSrcDesktop',
            'Background Image URL (Desktop)'
          ),
          stringInputSpec(
            'backgroundImageSrcTablet',
            'Background Image URL (Tablet)'
          ),
          stringInputSpec(
            'backgroundImageSrcMobile',
            'Background Image URL (Mobile)'
          ),
          boolCheckboxSpec('useParallax', 'Use Parallax Effect?'),
          ...parallaxInputConfig,
        ];

  return [
    ...BackgroundColor.getEditingConfig(componentDocument),
    textColorPicker,
    preheaderInput,
    stringTextAreaSpec('title', 'Title', {
      help: 'For a smaller size title, surround title text with | like |this|. For italics, surround title text with ! like !this!.',
    }),
    boolCheckboxSpec('fontChronicle', 'Title in Chronicle?'),
    stringSelectSpec('titleSize', 'Title Size', [
      { label: 'Default', value: 'default' },
      { label: 'Small', value: 'small' },
    ]),
    subtitleInput,
    stringTextAreaSpec('subtitleMobile', 'Mobile subtitle'),
    boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
      help: 'The Hero content defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
    }),
    stringSelectSpec(
      'selectHeroImageType',
      'Type of hero image',
      [
        { label: 'Background image', value: 'background' },
        { label: 'Standalone image', value: 'standalone' },
      ],
      {
        help: "Selecting 'Image' will place an image in the hero, while selecting 'Background Image' will place the image as a background image in the hero container. ",
      }
    ),
    ...heroImageTypeInput,
    boolCheckboxSpec('tall', 'Tall hero with extra padding?'),
    ...SmartButton.getEditingConfig(componentDocument),
    ...LinkButton.getEditingConfig(componentDocument),
    ...Disclosure.getEditingConfig(),
  ];
};

StandardHero.displayName = 'StandardHero';

export default connect(
  createStructuredSelector({
    size: property('mediaQuery'),
  })
)(StandardHero);
